import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';

const TrainingCatalogTable = ({ data, loading, onPage, lazyParams, totalCount, vehicleModelTextByCode = {} }) => {
    const { th } = useLanguage();
    const navigate = useNavigate();
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const normalizedData = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    }, [data]);

    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return normalizedData;

        return [...normalizedData].sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortOrder;
            if (bValue == null) return -sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortField.toLowerCase().includes('date')) {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -sortOrder;
            if (aValue > bValue) return sortOrder;
            return 0;
        });
    }, [normalizedData, sortField, sortOrder]);

    const handleSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    };

    const preExamBodyTemplate = (rowData) => (Number(rowData.preExam) === 1 ? th('common.yes', { fallback: 'Evet' }) : th('common.no', { fallback: 'Hayır' }));

    const isActiveBodyTemplate = (rowData) => (Number(rowData.isActive) === 0 ? th('common.inactive', { fallback: 'Pasif' }) : th('common.active', { fallback: 'Aktif' }));

    // Düzenleme artık modal değil, sayfa (bkz. `createEditForm/TrainingCatalogCreateEdit.jsx`) —
    // eğitmenler / içerik-kazanım / ön koşullar sekmeleri de o sayfanın alt kısmına taşındığı için
    // ayrı bir "detay" ikonuna gerek kalmadı, tek aksiyon düzenleme sayfasına gitmek.
    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')} onClick={() => navigate(`/learning-management-system/training-catalog/${rowData.id}`)} />
        </div>
    );

    return (
        <DataTable
            className="p-datatable-gridlines"
            value={sortedData}
            paginator
            rows={lazyParams.rows || 10}
            loading={loading}
            totalRecords={totalCount}
            showGridlines
            lazy
            scrollable
            scrollDirection="horizontal"
            first={lazyParams.first}
            onPage={onPage}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            dataKey="id"
            emptyMessage={th('trainingCatalog.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            tableStyle={{ minWidth: '110rem' }}
            sortMode="single"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
            <Column body={actionBodyTemplate} style={{ width: '5rem' }} align="center" frozen />
            <Column sortable field="trainingCode" header={th('trainingCatalog.columnCode')} style={{ width: '9rem' }} frozen />
            <Column sortable field="trainingName" header={th('trainingCatalog.columnName')} style={{ width: '16rem' }} />
            <Column sortable field="vehicleModel" header={th('trainingCatalog.columnVehicleModel')} style={{ width: '10rem' }} body={(row) => vehicleModelTextByCode[row.vehicleModel] || row.vehicleModel} />
            <Column sortable field="trainingTypeText" header={th('trainingCatalog.columnTrainingType')} style={{ width: '10rem' }} />
            <Column sortable field="trainingLocationText" header={th('trainingCatalog.columnTrainingLocation')} style={{ width: '10rem' }} />
            <Column sortable field="trainingLanguage" header={th('trainingCatalog.columnTrainingLanguage')} style={{ width: '9rem' }} />
            <Column sortable field="trainingDay" header={th('trainingCatalog.columnTrainingDay')} style={{ width: '10rem' }} />
            <Column sortable field="trainingDuration" header={th('trainingCatalog.columnTrainingDuration')} style={{ width: '8rem' }} />
            <Column sortable field="trainingCapacity" header={th('trainingCatalog.columnTrainingCapacity')} style={{ width: '8rem' }} />
            <Column field="preExam" header={th('trainingCatalog.columnPreExam')} style={{ width: '8rem' }} body={preExamBodyTemplate} />
            <Column field="isActive" header={th('common.status')} style={{ width: '8rem' }} body={isActiveBodyTemplate} />
            <Column sortable field="instructorsCount" header={th('trainingCatalog.columnInstructorsCount')} style={{ width: '8rem' }} />
            <Column sortable field="createdDate" header={th('trainingCatalog.columnCreatedDate')} style={{ width: '10rem' }} body={(row) => dateOnlyBodyTemplate(row, 'createdDate')} />
            <Column sortable field="createdName" header={th('trainingCatalog.columnCreatedName')} style={{ width: '10rem' }} />
        </DataTable>
    );
};

export default TrainingCatalogTable;
